// Step 23 — pure helpers for the contract intelligence workspace.
//
// All logic here is deterministic and side-effect free. It composes
// data the backend already emits (`contract_summary_dict` /
// `contract_detail_dict` from `backend/ai_native/contracts/serializers.py`)
// into shapes the contract components render.
//
// Guardrails:
//   - No invented completion percentage, "quality score", "confidence"
//     or "clause coverage". Every column shown is a value the backend
//     already returned.
//   - `fact_or_inference` labels — where present — are preserved
//     VERBATIM (backend does not currently emit them on contracts, but
//     if a future migration adds them, our findings shape carries them
//     through untouched).
//   - Provenance objects on `variables[]` are passed through untouched.
//     If a variable has no provenance, the finding's `source` is null
//     and the evidence panel renders "Ingen provenienskedja finns
//     registrerad" (from Step 19 AIEvidencePanel).
//   - Attention flags are *deterministic observations* — one chip per
//     signal, never a synthesized numeric score.
//   - No lifecycle verbs (approve/reject/send/sign/cancel/supersede)
//     are ever named in the prompt builders or ask hints.

import { KIND, SEVERITY } from "../intelligence/helpers";

// ── Status label map (matches contracts/lifecycle.py STATUS_CHOICES) ─
//
// The backend already returns Swedish choice labels via CHOICES, but
// tool payloads and list rows sometimes carry only the raw enum key.
// We keep a local mirror so the UI can render a friendly label
// regardless of which surface the row came from.

export const CONTRACT_STATUS_LABEL = {
  drafting: "Utkast",
  pending_review: "Under granskning",
  approved: "Godkänd",
  sent_for_signature: "Skickad för signering",
  signed: "Signerad",
  rejected: "Avvisad",
  cancelled: "Avbruten",
  superseded: "Ersatt",
};

export const CONTRACT_KIND_LABEL = {
  nda: "NDA / sekretessavtal",
  loi: "LOI / intentionsavtal",
  spa: "SPA / aktieköpsavtal",
  apa: "APA / inkråmsöverlåtelse",
  dd_agreement: "DD-avtal",
  management_agreement: "Ledningsavtal",
  advisory_agreement: "Rådgivaravtal",
  other: "Annat / anpassat",
};

// Statuses where the contract is STILL EDITABLE. Mirrors
// contracts.lifecycle.EDITABLE_STATUSES — we only use this to decide
// which attention chips apply, never to enable/disable a lifecycle
// button (the intelligence workspace has no such buttons).
export const EDITABLE_STATUSES = new Set(["drafting", "pending_review"]);

// Terminal statuses — mirrors contracts.lifecycle.TERMINAL_STATUSES.
export const TERMINAL_STATUSES = new Set([
  "signed",
  "rejected",
  "cancelled",
  "superseded",
]);


export function contractStatusLabel(status) {
  return CONTRACT_STATUS_LABEL[status] || status || "Okänd";
}

export function contractKindLabel(kind) {
  return CONTRACT_KIND_LABEL[kind] || kind || "Okänd";
}


// ── Attention flags ────────────────────────────────────────────────
//
// Each flag is a deterministic derivation from data the backend
// already gave us. We render these as chips; we never combine them
// into a "quality score". Missing/unknown fields simply mean the
// corresponding chip is not emitted — we never guess.

export function contractAttentionFlags(row) {
  if (!row) return [];
  const flags = [];

  if (row.status === "pending_review") {
    flags.push({
      kind: "pending_review",
      severity: SEVERITY.MEDIUM,
      label: "Under granskning",
      title:
        "Kontraktet väntar på mänsklig granskning. AI kan inte godkänna.",
    });
  }

  const uCount = Number(
    row.uncertainties_count ?? (row.uncertainties || []).length ?? 0,
  );
  if (uCount > 0) {
    flags.push({
      kind: "uncertainties",
      severity: SEVERITY.MEDIUM,
      label: `${uCount} osäkerhet${uCount === 1 ? "" : "er"}`,
      title:
        "AI-drafting flaggade poster som en människa måste lösa innan " +
        "godkännande.",
    });
  }

  if (row.status === "drafting") {
    flags.push({
      kind: "drafting",
      severity: SEVERITY.LOW,
      label: "Utkast",
      title:
        "Utkastet är fortfarande i drafting-läge. Ingen mänsklig " +
        "granskning påbörjad.",
    });
  }

  // Terminal outcomes are informational — highlighted so the reader
  // sees at a glance whether the row is done or blocked.
  if (row.status === "rejected") {
    flags.push({
      kind: "rejected",
      severity: SEVERITY.HIGH,
      label: "Avvisad",
      title: "Kontraktet avvisades av en granskare.",
    });
  }
  if (row.status === "cancelled") {
    flags.push({
      kind: "cancelled",
      severity: SEVERITY.HIGH,
      label: "Avbruten",
      title: "Kontraktsprocessen avbröts.",
    });
  }
  if (row.status === "superseded") {
    flags.push({
      kind: "superseded",
      severity: SEVERITY.LOW,
      label: "Ersatt",
      title: "Kontraktet har ersatts av ett nyare utkast.",
    });
  }

  return flags;
}


// ── Variables with provenance ──────────────────────────────────────
//
// `contract_detail_dict.variables` is a list of
//   {tag_name, value, provenance: {source_type, source_id, label}}
// (per the AI drafting schema in backend/ai_native/contracts/drafting.py).
//
// This helper normalizes shapes so the UI can render both:
//   - the value (with an explicit "Ej fastställt" placeholder when
//     `value` is empty/null — never a fabricated default), and
//   - the provenance chain (untouched).

export function normalizeContractVariables(variables) {
  if (!Array.isArray(variables)) return [];
  return variables
    .filter((v) => v && typeof v === "object")
    .map((v) => ({
      tag_name: v.tag_name || "",
      raw_value: v.value,
      display_value: _formatVariableValue(v.value),
      is_resolved: _isResolvedValue(v.value),
      provenance: v.provenance || null,
    }));
}

function _isResolvedValue(v) {
  if (v == null) return false;
  if (typeof v === "string" && v.trim() === "") return false;
  return true;
}

function _formatVariableValue(v) {
  if (v == null) return "Ej fastställt";
  if (typeof v === "string") {
    const trimmed = v.trim();
    return trimmed === "" ? "Ej fastställt" : trimmed;
  }
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}


// ── Uncertainties ──────────────────────────────────────────────────
//
// `contract_detail_dict.uncertainties` is free-form — the backend
// simply stores whatever the AI drafter emitted (usually a list of
// short strings or `{label, detail}` objects). We normalize into a
// stable shape without inventing structure that isn't there.

export function normalizeContractUncertainties(uncertainties) {
  if (!Array.isArray(uncertainties)) return [];
  return uncertainties
    .map((u, idx) => {
      if (u == null) return null;
      if (typeof u === "string") {
        const text = u.trim();
        if (!text) return null;
        return { key: `u.${idx}`, label: text, detail: null };
      }
      if (typeof u === "object") {
        const label =
          (u.label && String(u.label).trim()) ||
          (u.text && String(u.text).trim()) ||
          (u.tag_name && String(u.tag_name).trim()) ||
          "Okänd osäkerhet";
        const detail =
          (u.detail && String(u.detail).trim()) ||
          (u.reason && String(u.reason).trim()) ||
          null;
        return { key: `u.${idx}`, label, detail };
      }
      return null;
    })
    .filter(Boolean);
}


// ── Clauses included ───────────────────────────────────────────────
//
// `contract_detail_dict.clauses_included` is free-form too — usually
// `[{clause_name, source}]`. We render only what actually exists;
// missing sources stay missing.

export function normalizeContractClauses(clauses) {
  if (!Array.isArray(clauses)) return [];
  return clauses
    .map((c, idx) => {
      if (c == null) return null;
      if (typeof c === "string") {
        const name = c.trim();
        if (!name) return null;
        return { key: `c.${idx}`, name, source: null };
      }
      if (typeof c === "object") {
        const name =
          (c.clause_name && String(c.clause_name).trim()) ||
          (c.name && String(c.name).trim()) ||
          "Okänd klausul";
        const source =
          (c.source && String(c.source).trim()) ||
          (c.origin && String(c.origin).trim()) ||
          null;
        return { key: `c.${idx}`, name, source };
      }
      return null;
    })
    .filter(Boolean);
}


// ── Findings for AIFindingCard ──────────────────────────────────────
//
// The intelligence workspace already has a shared FindingCard shape
// (Step 19). We project contract data into that shape so the same
// visual language applies here.
//
// Kinds used:
//   - CONTRACT_REVIEW      — pending_review status
//   - CONTRACT_UNCERTAINTY — one per uncertainty row
//   - PREPAREDNESS         — variable missing provenance (a real gap
//                            in what the AI drafter could resolve
//                            from the dataroom / deal state)

export function contractFindingsFromDetail(detail) {
  if (!detail || typeof detail !== "object") return [];
  const out = [];
  const title = detail.title || `Kontrakt #${detail.id}`;
  const kindLabel = contractKindLabel(detail.kind);
  const contractSource = {
    source_type: "contract_draft",
    source_id: String(detail.id),
    label: title,
    as_of: detail.updated_at || null,
  };

  if (detail.status === "pending_review") {
    out.push({
      kind: KIND.CONTRACT_REVIEW,
      severity: SEVERITY.MEDIUM,
      title: `${title} väntar granskning`,
      summary:
        "En människa behöver godkänna eller avvisa utkastet i " +
        "granskningsvyn. AI kan inte godkänna.",
      category: "Kontrakt",
      source: contractSource,
      evidence: [],
      contract_id: detail.id,
      user_action_required: true,
      ask_prompt:
        `Granska avtalsutkastet "${title}" (${kindLabel}). Använd ` +
        `get_contract_draft för att hämta variabler, osäkerheter och ` +
        `klausuler. Peka på vad som saknas eller behöver mänskligt beslut.`,
      key: `contract.review.${detail.id}`,
    });
  }

  const uncertainties = normalizeContractUncertainties(detail.uncertainties);
  for (const u of uncertainties) {
    out.push({
      kind: KIND.CONTRACT_UNCERTAINTY,
      severity: SEVERITY.MEDIUM,
      title: `${title}: ${u.label}`,
      summary: u.detail || null,
      category: "Osäkerhet",
      source: contractSource,
      evidence: [],
      contract_id: detail.id,
      ask_prompt:
        `Förklara osäkerheten "${u.label}" i avtalsutkastet "${title}". ` +
        `Använd get_contract_draft och search_company_documents för att ` +
        `hitta underlag. Distinguish FAKTA från INFERENS.`,
      key: `contract.uncertainty.${detail.id}.${u.key}`,
    });
  }

  const variables = normalizeContractVariables(detail.variables);
  for (const v of variables) {
    if (v.is_resolved && v.provenance) continue;
    // A variable that either has no value OR no provenance is a real
    // gap — worth surfacing so the human reviewer can decide.
    const missing = [];
    if (!v.is_resolved) missing.push("värde saknas");
    if (!v.provenance) missing.push("provenienskedja saknas");
    out.push({
      kind: KIND.PREPAREDNESS,
      severity: v.is_resolved ? SEVERITY.LOW : SEVERITY.MEDIUM,
      title: `${title}: ${v.tag_name || "variabel"} — ${missing.join(", ")}`,
      summary: v.is_resolved
        ? `Värdet är satt (${v.display_value}) men ingen källa är ` +
          "registrerad."
        : "AI-drafting kunde inte fastställa ett värde för denna variabel.",
      category: "Variabel",
      source: contractSource,
      evidence: [],
      contract_id: detail.id,
      ask_prompt:
        `Var kommer variabeln ${v.tag_name} i avtalsutkastet "${title}" ` +
        `ifrån? Använd get_contract_draft och search_company_documents ` +
        `för att verifiera underlag.`,
      key: `contract.variable.${detail.id}.${v.tag_name || "unknown"}`,
    });
  }

  return out;
}


// ── Comparison-table row projection ────────────────────────────────

export function contractRowsForComparison(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((c) => ({
    id: c.id,
    title: c.title || `#${c.id}`,
    kind: c.kind,
    kind_label: contractKindLabel(c.kind),
    status: c.status,
    status_label: contractStatusLabel(c.status),
    source: c.source,
    acquirer_id: c.acquirer_id,
    acquirer_name: c.acquirer_name,
    variables_count: Number(c.variables_count ?? 0),
    uncertainties_count: Number(c.uncertainties_count ?? 0),
    clauses_count: Number(
      c.clauses_count ?? (c.clauses_included || []).length ?? 0,
    ),
    updated_at: c.updated_at,
    approved_at: c.approved_at,
    signed_at: c.signed_at,
    has_approved_pdf: !!(c.has_approved_pdf || c.approved_body_hash),
    is_editable: EDITABLE_STATUSES.has(c.status),
    is_terminal: TERMINAL_STATUSES.has(c.status),
  }));
}


// ── Ask Qada prompt builders ───────────────────────────────────────
//
// Contract-scoped prompts. Deliberately do NOT ask the agent to
// approve / reject / send / sign — those verbs have no tool exposure
// and the prompt should not suggest they do.

export function buildContractAskPrompt({
  contractId,
  contractTitle,
  contractKind,
  workspaceName,
  question,
}) {
  const title = contractTitle || `#${contractId}`;
  const kindLabel = contractKind ? ` (${contractKindLabel(contractKind)})` : "";
  const workspace = workspaceName ? ` i affären ${workspaceName}` : "";
  const suffix = question ? `\n\n${question}` : "";
  return (
    `Granska avtalsutkastet "${title}"${kindLabel}${workspace}. Använd ` +
    `get_contract_draft för att hämta variabler, osäkerheter och ` +
    `klausuler. Använd search_company_documents och get_deal_situation ` +
    `när du behöver underlag. Fokusera på öppna osäkerheter, obestämda ` +
    `variabler, underlag och vad som behöver mänskligt beslut. Bevara ` +
    `status-etiketten VERBATIM och skilj FAKTA / DETERMINISTISK ` +
    `SYSTEMOBSERVATION / INFERENS / FÖRESLAGEN ÅTGÄRD / MÄNSKLIGT ` +
    `BESLUT.${suffix}`
  );
}

export function buildContractComparisonAskPrompt({
  workspaceName,
  question,
}) {
  const workspace = workspaceName ? ` i affären ${workspaceName}` : "";
  const suffix = question ? `\n\n${question}` : "";
  return (
    `Jämför avtalsutkasten${workspace}. Använd list_contract_drafts för ` +
    `översikten och get_contract_draft per utkast där det behövs. ` +
    `Prioritera utkast som är i pending_review eller har osäkerheter. ` +
    `Bevara status-etiketten VERBATIM och separera FAKTA från ` +
    `SYSTEMOBSERVATION från INFERENS.${suffix}`
  );
}
